import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import api from '../../../API';
import { isCaseScope } from '../utils/blockScope';

/**
 * Persist region definition details (and identity fields when provided).
 * @param {import('../utils/blockScope').BlockScope} [scope]
 */
export function useRegionMutations(scope) {
    const queryClient = useQueryClient();

    const saveRegion = useCallback(async (modifiedDefinition) => {
        if (!modifiedDefinition?._id) {
            throw new Error('Region id is required');
        }
        const body = {
            abbreviation: modifiedDefinition.abbreviation ?? modifiedDefinition.name,
            display_name: modifiedDefinition.displayName ?? modifiedDefinition.longName,
            schema_region_key: modifiedDefinition.schemaRegionKey ?? null,
            schema_id: modifiedDefinition.schemaId ?? null,
            description: modifiedDefinition.description ?? null,
            details: modifiedDefinition.details ?? {},
            used_from: modifiedDefinition.usedFrom ?? null,
            used_to: modifiedDefinition.usedTo ?? null,
        };
        const response = await api.put(`/bdsa/region/${modifiedDefinition._id}`, { body });
        if (response.status !== 200) {
            const msg = response.data?.message || response.data?.error || 'Failed to save region';
            throw new Error(typeof msg === 'string' ? msg : 'Failed to save region');
        }
        queryClient.invalidateQueries({ queryKey: ['bdsa-region'] });
        queryClient.invalidateQueries({ queryKey: ['bdsa-metadata-options'] });
        if (modifiedDefinition.bdsaBlockId) {
            queryClient.invalidateQueries({
                queryKey: ['bdsa-region', 'block', modifiedDefinition.bdsaBlockId],
            });
        }
        if (isCaseScope(scope) && scope.caseId && scope.setDoc) {
            queryClient.invalidateQueries({ queryKey: ['bdsa-case', scope.caseId] });
            const caseRes = await api.get('bdsa/case', { query: { networkIdentifier: scope.caseId } });
            if (caseRes?.status === 200 && caseRes?.data) {
                scope.setDoc(caseRes.data);
            }
        } else {
            queryClient.invalidateQueries({ queryKey: ['bdsa-block'] });
        }
        return response.data;
    }, [scope, queryClient]);

    return { saveRegion };
}
